import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import Wordmark from "@/components/Logo";
import DroneFlight from "@/components/DroneFlight";
import { scrollToSection, waLink } from "@/lib/scroll";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const fade = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.14]);
  const bgOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.25]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative min-h-screen bg-[#151A1F] overflow-hidden flex flex-col">
      <motion.div style={{ scale: bgScale, opacity: bgOpacity }} className="absolute inset-0" aria-hidden="true">
        <motion.img
          src="/fotos/portada-soya-hileras.webp"
          alt=""
          fetchPriority="high"
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-[#151A1F]/70" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#151A1F] via-transparent to-[#151A1F]/70" aria-hidden="true" />
      <div className="absolute inset-0 hairlines opacity-40" aria-hidden="true" />

      <DroneFlight targetRef={ref} />

      <div className="relative flex-1 flex flex-col justify-center mx-auto max-w-7xl px-6 sm:px-10 pt-36 sm:pt-40 pb-16 w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="flex flex-wrap items-center gap-x-6 gap-y-2 font-mono-tech text-[11px] uppercase tracking-[0.25em] text-[#00A651]"
        >
          <span data-testid="hero-status-badge" className="inline-flex items-center gap-2 border border-[#00A651]/40 bg-[#151A1F]/50 backdrop-blur-sm rounded-full px-4 py-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00A651] animate-pulse" />
            Campaña de verano · Nov — Feb
          </span>
          <span className="text-[#F6F7F5]/50 hidden sm:inline">SCZ · 17°47'S 63°10'W</span>
          <span className="text-[#F6F7F5]/50 hidden md:inline">DERIVA CALIBRADA — V1.0</span>
        </motion.div>

        <motion.div style={{ opacity: fade }} className="mt-12 sm:mt-16 [filter:drop-shadow(0_4px_28px_rgba(0,0,0,0.65))]">
          <Wordmark tone="light" animated className="text-[16vw] sm:text-[13vw] lg:text-[11vw]" />
          <p className="font-mono-tech text-[11px] sm:text-xs uppercase tracking-[0.35em] text-[#F6F7F5]/70 mt-3">
            Aplicación agrícola con drones
          </p>
        </motion.div>

        <h1 className="font-display font-bold tracking-tight leading-[1.04] text-4xl sm:text-6xl lg:text-7xl mt-12 sm:mt-16 max-w-5xl [text-shadow:0_2px_24px_rgba(0,0,0,0.7)]">
          <MaskedLine delay={0.9}>Protegé tu cosecha</MaskedLine>
          <MaskedLine delay={1.05}>
            <span className="text-[#00A651]">sin pisar el cultivo.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 max-w-xl text-base sm:text-lg text-[#F6F7F5]/85 leading-relaxed [text-shadow:0_1px_14px_rgba(0,0,0,0.7)]"
        >
          Aplicación de precisión con dron para soya, sorgo, maíz, caña y girasol en Santa Cruz.
          Diagnóstico de finca con IA gratis y precio por hectárea el mismo día.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.45, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            data-testid="hero-cta-cotizar"
            href={waLink("Hola Fumiga, quiero cotizar aplicación con dron para mis hectáreas.")}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#FFB000] text-[#151A1F] font-display font-semibold px-8 py-4 rounded-full text-base hover:scale-[1.03] transition-transform duration-300 shadow-[0_10px_40px_rgba(255,176,0,0.3)]"
          >
            Cotizar por WhatsApp
          </a>
          <button
            data-testid="hero-cta-ia"
            onClick={() => scrollToSection("ia")}
            className="border border-[#F6F7F5]/30 bg-[#151A1F]/40 backdrop-blur-sm text-[#F6F7F5] font-display font-medium px-8 py-4 rounded-full text-base hover:border-[#00A651] hover:text-[#00A651] transition-colors duration-300"
          >
            Diagnóstico de finca con IA
          </button>
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-down"
        onClick={() => scrollToSection("prueba")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.1 }}
        className="relative mx-auto max-w-7xl px-6 sm:px-10 pb-10 w-full flex items-center gap-3 font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#F6F7F5]/60 hover:text-[#00A651] transition-colors"
        aria-label="Bajar a los números"
      >
        <ArrowDown size={16} className="animate-bounce" />
        Los números que importan
      </motion.button>
    </section>
  );
};

export default Hero;
